import React, { useEffect, useRef, useState, useContext } from "react"
import PropTypes from "prop-types"
import { Link, useStaticQuery, graphql } from "gatsby"
import { Location } from "@reach/router"
import tw, { styled } from "twin.macro"
import { motion, AnimatePresence } from "framer-motion"
import { useMediaQuery } from "react-responsive"
import moment from "moment"

import Lightbox from "components/lightbox"
import Search from "components/search/instant"
import MenuButton from "components/menu-button"
import MoreLink from "components/more-link"
import LinkTo from "components/link-to"
import CouponContext from "context/coupon-context"
import StickyHeaderContext from "context/sticky-header-context"
import ProductsMenu from "components/header/products-menu"
import IndustryMenu from "components/header/industry-menu"
import MenuGroup from "components/header/menu-group"
import MenuTitle from "components/header/menu-title"
import MenuItems from "components/header/menu-items"
import PressMoreInfo from "components/press-more-info"

import Logomark from "images/logomark-dark.inline.svg"
import Logotype from "images/logotype-dark.inline.svg"
import DemandAcademyLogo from "images/logos/demand-academy.svg"
import DemandAcademyBodyBg from "images/bg/demand-academy-body.jpg"
import IconSearch from "images/icons/search.inline.svg"
import IconYoutube from "images/icons/youtube.inline.svg"
import IconLinkedin from "images/icons/linkedin.inline.svg"
import IconInstagram from "images/icons/instagram.inline.svg"
import IconTwitter from "images/icons/twitter.inline.svg"
import IconLogin from "images/icons/demand360-login.inline.svg"
import DownArrow from "images/icons/arrow-down-bold.inline.svg"

const Nav = styled.div`
  ${tw`w-screen z-10 top-0 text-white font-body fixed`}
  font-size: 12px;
  transition: all 0.2s;

  background-color: ${(props) =>
    props.sticky || props.demandAcademy ? "black" : "transparent"};
  z-index: 49;
  height: 50px;
  ${({ demandAcademy }) => (demandAcademy ? "padding-top: 0;" : "")}

  @media (min-width: 640px) {
    height: 80px;
  }

  button > svg,
  a > svg {
    path {
      fill: #d9d9d9;
    }
    transition: all 0.2s;

    &:hover {
      path {
        fill: #ffffff;
      }
    }
  }

  & > .content-wrapper {
    ${tw`mx-auto flex flex-row items-center justify-between h-full`}

    ${({ demandAcademy }) => (demandAcademy ? "padding-top: 4px;" : "")}

    @media (min-width: 640px) {
      padding-left: 30px;
      padding-right: 40px;
    }
  }
`
const Logo = styled.div`
  ${tw`pl-3 flex flex-row justify-center`}

  @media (min-width: 1200px) {
    height: 54px;
  }

  svg {
    height: 35px;

    @media (min-width: 1200px) {
      height: 54px;
      padding-top: 7px;
      padding-bottom: 7px;
    }
  }
`

const LogotypeContainer = styled.div`
  ${tw`hidden m:block`}
  transition: opacity 0.7s;
  opacity: 1;
`

const UnderlinedNav = styled.div`
  ${tw`mx-3 uppercase font-body text-white border-neonred`}
  border-bottom-width: 2px;
`

const MainMenu = styled.div`
  ${tw`mx-auto min-h-screen flex flex-col justify-between pb-10 pt-20 m:pt-32 px-4 l:px-2 w-full l:max-w-l font-body text-white`}

  background-color: #0f0f0f;
  transition: opacity 0.2 ease, height 0.5 ease-out;
`

const MenuGroupWrapper = styled.div`
  @media (min-width: 640px) {
    height: 90vh;
  }
  @media (min-width: 1024px) {
    height: 85vh;
  }
  @media (min-width: 1200px) {
    height: 650px;
  }
`

const ParrotButton = styled.button`
  ${tw`bg-springgreen rounded p-2 px-3 uppercase font-display text-sm mx-4 text-white`}

  background: linear-gradient(180deg, #05C56B 0%, #00933E 100%);

  &:hover {
    background: linear-gradient(0deg, #05c56b 0%, #00933e 100%);
  }

  &.red {
    background: linear-gradient(180deg, #e3284c 0%, #aa0024 100%);
    box-shadow: 0px 2px 10px rgba(227, 40, 76, 0.6);

    &:hover {
      background: linear-gradient(0deg, #e3284c 0%, #aa0024 100%);
    }
  }

  &.secondary {
    ${tw`font-bold`}

    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.14) 0%,
      rgba(255, 255, 255, 0.1) 100%
    );
    backdrop-filter: blur(2px);
    color: #68f8a0;
    border-radius: 5px;

    &:hover {
      background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.14) 0%,
        rgba(255, 255, 255, 0.1) 100%
      );
    }
  }
`

const NavLinkTitle = styled.span`
  ${tw`pb-1 font-body text-sm uppercase`}
  border-bottom-width: 0;
  position: relative;
  transition: all 0.2s;
  letter-spacing: 0.4px;

  .link-underline {
    content: "";
    position: absolute;
    bottom: -2px;
    width: 0px;
    height: 2px;
    margin: 0;
    transition: all 0.2s ease-in-out;
    opacity: 0;
    left: 0;
  }

  &:hover,
  &.active {
    color: #ffffff;

    .link-underline {
      width: 100%;
      opacity: 1;
    }
  }

  &.product {
    ${tw`flex flex-row items-center cursor-pointer pb-0`}
  }
  &.industry {
    ${tw`flex flex-row items-center cursor-pointer pb-0`}
  }
`

const NavLink = ({
  linkInfo,
  children,
  underline,
  className = "",
  hovered = false,
  ...rest
}) => (
  <Location>
    {({ location }) => {
      const path = location.pathname.replace(/^\//, "")
      const to = linkInfo.url

      const active =
        hovered ||
        path === to ||
        (path.startsWith("platform") && to === "platform") ||
        (path.startsWith("industry") && to === "industry") ||
        (path.startsWith("insights") && to === "insights") ||
        (path.startsWith("products") &&
          to === "/#products" &&
          // never highlight content valuation in products section as it is a first class citizen in the menu
          !path.startsWith("products/streaming-economics"))

      return (
        <LinkTo linkInfo={linkInfo} className="mx-4">
          <NavLinkTitle
            className={`${className} ${active ? "active" : ""}`}
            underline={underline}
            {...rest}
          >
            {children}
            <div className={`link-underline bg-${underline || "neonred"}`} />
          </NavLinkTitle>
        </LinkTo>
      )
    }}
  </Location>
)

const DemandAcademyStyles = styled.div`
  ${tw`hidden l:block fixed left-0 w-full`}

  height: 50px;
  transition: all 0.2s;

  top: 80px;
  z-index: 49;
  background-color: black;

  .demand-academy-logo {
    ${tw`h-full w-full flex flex-row items-center justify-center`}

    img {
      height: 12px;
    }
  }
`

const DemandAcademyBgSection = styled.div`
  background: linear-gradient(
      0deg,
      rgba(95, 57, 203, 0.23),
      rgba(95, 57, 203, 0.23)
    ),
    ${({ bg }) => `url(${bg})`};
  background-repeat: no-repeat;
  background-size: 100% auto;
  background-position: center center;
  background-color: black;
`

function DemandAcademyHeader({ bg, sticky }) {
  return (
    <DemandAcademyStyles sticky={sticky}>
      <DemandAcademyBgSection bg={bg} className="demand-academy-logo">
        <Link to="/academy">
          <img src={DemandAcademyLogo} alt="" />
        </Link>
      </DemandAcademyBgSection>
    </DemandAcademyStyles>
  )
}

function ContentWrapper({ bg, children }) {
  return (
    <>
      {bg && (
        <DemandAcademyBgSection bg={bg} className="content-wrapper">
          {children}
        </DemandAcademyBgSection>
      )}
      {!bg && <div className="content-wrapper">{children}</div>}
    </>
  )
}

const DemandAcademyLogoContainer = styled.div`
  @media (min-width: 1200px) {
    ${tw`hidden`}
  }

  img {
    height: 10px;

    @media (min-width: 640px) {
      height: 13px;
    }
  }
`

function getLocationContext(path) {
  if (path.startsWith("/products/demand360/lite")) {
    return "demand360Lite"
  } else if (path.startsWith("/products")) {
    return "product"
  } else if (path.startsWith("/newsroom")) {
    return "newsroom"
  } else if (path.startsWith("/industry")) {
    return "industry"
  } else {
    return "other"
  }
}

function actionInfo(location, specialOffer) {
  const locationContext = getLocationContext(location.pathname)
  const contactUs =
    locationContext === "product" ||
    locationContext === "newsroom" ||
    locationContext === "industry"

  return {
    to:
      locationContext === "demand360Lite"
        ? "/start/"
        : contactUs
        ? "/contact"
        : "/contact",
    label:
      locationContext === "demand360Lite"
        ? specialOffer === null
          ? "Subscribe Now"
          : "Special Offer"
        : contactUs
        ? "Contact Us"
        : "Contact Us",
    color: contactUs ? "secondary" : "green",
  }
}

const SocialIcons = ({ className = "" }) => {
  return (
    <div className={`flex flex-row items-center ${className}`}>
      <a href="https://www.youtube.com/channel/UCAAJjdGWv_Xm5OK7wAzhxmg">
        <IconYoutube className="flex-1 mr-2 m:mr-1" />
      </a>
      <a href="https://www.linkedin.com/company/parrot-analytics/">
        <IconLinkedin className="flex-1 mx-2 m:mx-1" />
      </a>
      <a href="https://www.instagram.com/globaltvdemandawards/">
        <IconInstagram className="flex-1 mx-2 m:mx-1" />
      </a>
      <a href="https://twitter.com/ParrotAnalytics">
        <IconTwitter className="flex-1 mx-2 m:mx-1" />
      </a>
    </div>
  )
}

const Header = ({
  collapse,
  open = false,
  demandAcademy = false,
  forceStuck = false,
}) => {
  const [menu, showMenu] = useState(open)
  const [search, searchActive] = useState(false)
  const [mobileSearch, mobSearchActive] = useState(false)
  const [previousScrollY, setScroll] = useState(0)
  const [specialOffer, setSpecialOffer] = useState(null)
  const [prodLinkHover, setProdLinkHover] = useState(false)
  const [industryLinkHover, setIndustryLinkHover] = useState(false)
  const [prodMenuHover, setProdMenuHover] = useState(false)
  const [industryMenuHover, setIndustryMenuHover] = useState(false)
  const [showDaBg, setShowDaBg] = useState(false)

  const searchContainer = useRef()

  const couponContext = useContext(CouponContext)
  const { isStuck: isStuckContent } = useContext(StickyHeaderContext)

  const isStuck = isStuckContent || forceStuck

  const isDesktop = useMediaQuery({ minWidth: 1200 })

  useEffect(() => {
    async function fetchSpecialOffer() {
      setSpecialOffer(await couponContext.getSpecialOffer())
    }

    fetchSpecialOffer()
  }, [couponContext])

  useEffect(() => {
    setShowDaBg(demandAcademy && !isDesktop)
  }, [isDesktop])

  const data = useStaticQuery(graphql`
    query {
      gcms {
        applicationsConnection(orderBy: title_ASC) {
          edges {
            node {
              id
              title
              slug
            }
          }
        }
        industriesConnection(orderBy: order_ASC) {
          edges {
            node {
              id
              title
              slug
            }
          }
        }
        headerMenus: menus(where: { menuLocation: HeaderLinks }) {
          menuItems {
            id
            title
            url
            isExternal
            openInNewTab
          }
        }
        mainMenus: menus(
          orderBy: order_ASC
          where: { menuLocation: MainMenu }
        ) {
          id
          title
          url
          isExternal
          order
          menuItemsSource
          menuItems {
            id
            title
            url
            isExternal
            openInNewTab
          }
        }
        productMenus: menus(
          orderBy: order_ASC
          where: { menuLocation: ProductLinks }
        ) {
          id
          title
          url
          isExternal
          order
          menuItemsSource
          menuItems {
            id
            title
            url
            isExternal
            openInNewTab
          }
        }
        industryMenus: menus(
          orderBy: order_ASC
          where: { menuLocation: IndustryLinks }
        ) {
          id
          title
          url
          isExternal
          order
          menuItemsSource
          menuItems {
            id
            title
            url
            isExternal
            openInNewTab
          }
        }
      }
    }
  `)
  const applications = data.gcms.applicationsConnection.edges
  const industries = data.gcms.industriesConnection.edges

  const { headerMenus, mainMenus, productMenus, industryMenus } = data.gcms

  const headerLinks = headerMenus[0]?.menuItems || []

  const prodMenuActive = prodLinkHover || prodMenuHover
  const industryMenuActive = industryLinkHover || industryMenuHover

  function menuClick() {
    showMenu(!menu)
    collapse && collapse(!menu)
    if (menu) {
      window.scrollTo({ top: previousScrollY })
    } else {
      setScroll(window.scrollY)
      window.scrollTo({ top: 0 })
    }
  }

  function closeSearch() {
    collapse && collapse(false)
    searchActive(false)
    mobSearchActive(false)
    showMenu(false)
  }

  function mobSearch(active) {
    mobSearchActive(active)
  }

  return (
    <>
      {!search && (
        <>
          <Nav
            id="header"
            sticky={isStuck || prodMenuActive || industryMenuActive}
            demandAcademy={demandAcademy}
          >
            <ContentWrapper bg={showDaBg ? DemandAcademyBodyBg : null}>
              <Link to="/">
                <Logo>
                  <Logomark />
                  <LogotypeContainer show={!isStuck}>
                    <Logotype />
                  </LogotypeContainer>
                </Logo>
              </Link>
              {demandAcademy && (
                <DemandAcademyLogoContainer>
                  <Link to="/academy">
                    <img src={DemandAcademyLogo} alt="" />
                  </Link>
                </DemandAcademyLogoContainer>
              )}
              <div className="flex flex-row items-center h-full">
                <div className="hidden l:flex flex-row items-center font-body text-base text-white h-full">
                  {!menu && (
                    <div className="flex items-center h-full">
                      <div
                        className="h-full flex flex-col justify-center"
                        onMouseEnter={() => setProdLinkHover(true)}
                        onMouseLeave={() => setProdLinkHover(false)}
                      >
                        <NavLink
                          className="product"
                          linkInfo={{ url: "/#products" }}
                          underline="neonred"
                          hovered={prodLinkHover}
                        >
                          Products
                          <DownArrow className="ml-2 w-3 mb-1" />
                        </NavLink>
                      </div>
                      <div
                          className="h-full flex flex-col justify-center"
                          onMouseEnter={() => setIndustryLinkHover(true)}
                          onMouseLeave={() => setIndustryLinkHover(false)}
                      >
                        <NavLink
                            className="industry"
                            linkInfo={{ url: "/industry" }}
                            underline="neonred"
                            hovered={industryLinkHover}
                        >
                          Industry
                          <DownArrow className="ml-2 w-3 mb-1" />
                        </NavLink>
                      </div>
                      {headerLinks.filter((menuItem) => menuItem.url !== "industry").map((menuItem) => (
                        <NavLink
                          linkInfo={menuItem}
                          key={menuItem.id}
                          underline="neonred"
                        >
                          {menuItem.title}
                        </NavLink>
                      ))}
                    </div>
                  )}
                  {menu && (
                    <a href={process.env.GATSBY_DEMAND360_LOGIN}>
                      <div className="flex flex-row items-center text-white uppercase font-display">
                        <IconLogin className="mr-2" />
                        <span className="demand360-brand">DEMAND360&nbsp;</span>
                        Login
                      </div>
                    </a>
                  )}
                  <div className="block flex-auto">
                    <Location>
                      {({ location }) => {
                        const { to, label, color } = actionInfo(
                          location,
                          specialOffer
                        )
                        return (
                          <Link to={to}>
                            <ParrotButton className={color}>
                              {label}
                            </ParrotButton>
                          </Link>
                        )
                      }}
                    </Location>
                  </div>
                </div>
                {!demandAcademy && (
                  <Location>
                    {({ location }) => {
                      const { to, label } = actionInfo(location, specialOffer)
                      return (
                        <Link to={to} className="l:hidden">
                          <UnderlinedNav>{label}</UnderlinedNav>
                        </Link>
                      )
                    }}
                  </Location>
                )}
                <div className="flex flex-row items-center mr-1">
                  <Link
                    id="search"
                    className="px-3 hidden m:block outline-none focus:outline-none focus:border-none focus:shadow-none"
                    to="/search/"
                    // onClick={openSearch}
                  >
                    <IconSearch />
                  </Link>
                  <button
                    id="nav-toggle"
                    className="pl-3 pr-1 outline-none focus:outline-none focus:border-none focus:shadow-none"
                    onClick={menuClick}
                  >
                    <MenuButton open={menu} />
                  </button>
                </div>
              </div>
            </ContentWrapper>
          </Nav>
          {demandAcademy && !menu && (
            <DemandAcademyHeader bg={DemandAcademyBodyBg} sticky={isStuck} />
          )}
          <AnimatePresence>
            {prodMenuActive && (
              <motion.div
                key="products-header-menu"
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed left-0 top-20 w-full h-80 z-50"
                onMouseEnter={() => setProdMenuHover(true)}
                onMouseLeave={() => setProdMenuHover(false)}
              >
                <ProductsMenu products={productMenus} />
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {industryMenuActive && (
                <motion.div
                    key="products-header-menu"
                    animate={{ opacity: 1 }}
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed left-0 top-20 w-full h-80 z-50"
                    onMouseEnter={() => setIndustryMenuHover(true)}
                    onMouseLeave={() => setIndustryMenuHover(false)}
                >
                  <IndustryMenu industry={industryMenus} />
                </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
      <AnimatePresence>
        {menu && (
          <motion.div
            key="industry-selector-motion"
            animate={{ opacity: 1, backgroundColor: "#0f0f0f" }}
            initial={{ opacity: 0, backgroundColor: "black" }}
            exit={{ opacity: 0, backgroundColor: "black" }}
            transition={{ duration: 0.2 }}
          >
            <MainMenu>
              <div className="m:hidden flex flex-col items-center pb-3">
                <hr />
                <div className="flex flex-row items-center">
                  <Location>
                    {({ location, history }) => (
                      <Search
                        collapse={mobSearch}
                        close={closeSearch}
                        autofocus={false}
                        location={location}
                        history={history}
                      />
                    )}
                  </Location>
                </div>
                <hr />
              </div>
              {!mobileSearch && (
                <div onClick={menuClick}>
                  <MenuGroupWrapper className="flex flex-wrap flex-col items-center l:items-start pt-0 pb-5">
                    {mainMenus.map((menu) => (
                      <MenuGroup key={menu.id}>
                        <LinkTo linkInfo={menu}>
                          <MenuTitle className="product">
                            <MoreLink product={true}>{menu.title}</MoreLink>
                          </MenuTitle>
                        </LinkTo>
                        <MenuItems
                          menu={menu}
                          industries={industries}
                          applications={applications}
                          className="product"
                        ></MenuItems>
                      </MenuGroup>
                    ))}
                  </MenuGroupWrapper>
                  <div>
                    <div className="font-display uppercase text-base text-center block m:hidden">
                      <hr />
                      <a href={process.env.GATSBY_DEMAND360_LOGIN}>
                        <div className="text-springgreen py-5 flex flex-row items-center justify-center font-display">
                          <IconLogin className="mr-2" />
                          <span className="demand360-brand">
                            DEMAND360&nbsp;
                          </span>
                          Login
                        </div>
                      </a>
                      <hr />
                      <PressMoreInfo className="flex py-6" />
                      <hr />
                      <div className="pt-5">
                        <Link to="/contact">Contact Us</Link>
                      </div>
                      <div className="text-grey-35 py-5">
                        <a
                          href="https://helpcenter.parrotanalytics.com/"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          Customer Support
                        </a>
                      </div>
                    </div>
                    <div className="flex flex-row justify-center m:justify-between py-4">
                      <PressMoreInfo className="hidden m:flex" />
                      <SocialIcons className="block m:hidden" />
                      <div id="help" className="flex flex-row">
                        <Link to="/contact">
                          <div className="font-display uppercase text-base mr-6 text-springgreen hidden m:block">
                            Contact Us
                          </div>
                        </Link>
                        <a
                          href="https://helpcenter.parrotanalytics.com/"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          <div className="font-display uppercase text-base hidden m:block">
                            Customer Support
                          </div>
                        </a>
                      </div>
                    </div>
                    <hr />
                    <div className="flex flex-row justify-center m:justify-between py-4">
                      <div
                        id="menu-footer"
                        className="text-center m:text-left font-body text-grey-35 text-xs m:w-1/3"
                      >
                        © 2011–{moment().format("YYYY")} Parrot Analytics
                        Limited. All rights reserved.
                      </div>
                      <div className="text-center font-body text-grey-35 text-xs hidden m:block w-1/3">
                        <Link to="/terms-of-service">Terms of Service</Link> |{" "}
                        <Link to="/privacy-policy">Privacy Policy</Link>
                      </div>
                      <SocialIcons className="hidden m:flex w-1/3 justify-end" />
                    </div>
                  </div>
                </div>
              )}
            </MainMenu>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!!search && (
          <motion.div
            key="search-overlay"
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute top-0 left-0"
          >
            <Lightbox
              className="z-40"
              key="search-lightbox"
              onClick={closeSearch}
              opacity={95}
            />
          </motion.div>
        )}
        {!!search && (
          <motion.div
            key="search-bg"
            animate={{ scaleY: 1 }}
            initial={{ scaleY: 0 }}
            exit={{ scaleY: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute top-0 left-0 w-full z-50 bg-white hidden m:block"
            style={{ height: 120, originY: "0px" }}
          ></motion.div>
        )}
        {!!search && (
          <motion.div
            key="search-box"
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="absolute top-0 w-full m:h-screen m:overflow-y-scroll z-50"
            id="search-box"
            ref={searchContainer}
          >
            <Location>
              {({ location, history }) => (
                <Search
                  container={searchContainer}
                  close={closeSearch}
                  autofocus={true}
                  location={location}
                  history={history}
                />
              )}
            </Location>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

Header.propTypes = {
  siteTitle: PropTypes.string,
}

Header.defaultProps = {
  siteTitle: ``,
}

export default Header
